import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { dashboardAPI, getDashboardApiUrl as getApiUrlForError } from '../services/api';
import { Edit, ExternalLink, Phone, Mail, Globe, MapPin, Plus, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import { Country } from 'country-state-city';
import { useAuth } from '../contexts/AuthContext';

// Comprehensive time zone list
const TIME_ZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'EST', label: 'EST (Eastern Standard Time, UTC-5)' },
  { value: 'EDT', label: 'EDT (Eastern Daylight Time, UTC-4)' },
  { value: 'CST', label: 'CST (Central Standard Time, UTC-6)' },
  { value: 'CDT', label: 'CDT (Central Daylight Time, UTC-5)' },
  { value: 'MST', label: 'MST (Mountain Standard Time, UTC-7)' },
  { value: 'MDT', label: 'MDT (Mountain Daylight Time, UTC-6)' },
  { value: 'PST', label: 'PST (Pacific Standard Time, UTC-8)' },
  { value: 'PDT', label: 'PDT (Pacific Daylight Time, UTC-7)' },
  { value: 'AKST', label: 'AKST (Alaska Standard Time, UTC-9)' },
  { value: 'AKDT', label: 'AKDT (Alaska Daylight Time, UTC-8)' },
  { value: 'HST', label: 'HST (Hawaii Standard Time, UTC-10)' },
  { value: 'GMT', label: 'GMT (Greenwich Mean Time)' },
  { value: 'GMT+1', label: 'GMT+1 (Central European Time)' },
  { value: 'GMT+2', label: 'GMT+2 (Eastern European Time)' },
  { value: 'GMT+3', label: 'GMT+3 (Moscow Time)' },
  { value: 'GMT+4', label: 'GMT+4 (Gulf Standard Time)' },
  { value: 'GMT+5', label: 'GMT+5 (Pakistan Standard Time)' },
  { value: 'GMT+5:30', label: 'GMT+5:30 (India Standard Time - IST)' },
  { value: 'GMT+6', label: 'GMT+6 (Bangladesh Standard Time)' },
  { value: 'GMT+7', label: 'GMT+7 (Indochina Time)' },
  { value: 'GMT+8', label: 'GMT+8 (China Standard Time)' },
  { value: 'GMT+9', label: 'GMT+9 (Japan Standard Time)' },
  { value: 'GMT+10', label: 'GMT+10 (Australian Eastern Time)' },
  { value: 'GMT+11', label: 'GMT+11 (Solomon Islands Time)' },
  { value: 'GMT+12', label: 'GMT+12 (New Zealand Time)' },
  { value: 'GMT-1', label: 'GMT-1 (Cape Verde Time)' },
  { value: 'GMT-2', label: 'GMT-2 (Mid-Atlantic Time)' },
  { value: 'GMT-3', label: 'GMT-3 (Argentina Time)' },
  { value: 'GMT-4', label: 'GMT-4 (Venezuela Time)' },
  { value: 'GMT-5', label: 'GMT-5 (Colombia Time)' },
  { value: 'GMT-6', label: 'GMT-6 (Central America Time)' },
  { value: 'GMT-7', label: 'GMT-7 (Mountain Time)' },
  { value: 'GMT-8', label: 'GMT-8 (Pacific Time)' },
  { value: 'GMT-9', label: 'GMT-9 (Alaska Time)' },
  { value: 'GMT-10', label: 'GMT-10 (Hawaii Time)' },
  { value: 'GMT-11', label: 'GMT-11 (Samoa Time)' },
  { value: 'GMT-12', label: 'GMT-12 (Baker Island Time)' },
  { value: 'AEST', label: 'AEST (Australian Eastern Standard Time)' },
  { value: 'AEDT', label: 'AEDT (Australian Eastern Daylight Time)' },
  { value: 'AWST', label: 'AWST (Australian Western Standard Time)' },
  { value: 'IST', label: 'IST (India Standard Time)' },
  { value: 'JST', label: 'JST (Japan Standard Time)' },
  { value: 'KST', label: 'KST (Korea Standard Time)' },
  { value: 'CET', label: 'CET (Central European Time)' },
  { value: 'CEST', label: 'CEST (Central European Summer Time)' },
  { value: 'EET', label: 'EET (Eastern European Time)' },
  { value: 'EEST', label: 'EEST (Eastern European Summer Time)' },
  { value: 'WET', label: 'WET (Western European Time)' },
  { value: 'WEST', label: 'WEST (Western European Summer Time)' },
  { value: 'BRT', label: 'BRT (Brasília Time)' },
  { value: 'BRST', label: 'BRST (Brasília Summer Time)' },
  { value: 'ART', label: 'ART (Argentina Time)' },
  { value: 'CLT', label: 'CLT (Chile Time)' },
  { value: 'CLST', label: 'CLST (Chile Summer Time)' },
  { value: 'SAST', label: 'SAST (South Africa Standard Time)' },
  { value: 'EAT', label: 'EAT (East Africa Time)' },
  { value: 'CAT', label: 'CAT (Central Africa Time)' },
  { value: 'WAT', label: 'WAT (West Africa Time)' },
  { value: 'MSK', label: 'MSK (Moscow Time)' },
  { value: 'SGT', label: 'SGT (Singapore Time)' },
  { value: 'HKT', label: 'HKT (Hong Kong Time)' },
  { value: 'PHT', label: 'PHT (Philippines Time)' },
  { value: 'THA', label: 'THA (Thailand Time)' },
  { value: 'ICT', label: 'ICT (Indochina Time)' },
  { value: 'WIB', label: 'WIB (Western Indonesia Time)' },
  { value: 'WITA', label: 'WITA (Central Indonesia Time)' },
  { value: 'WIT', label: 'WIT (Eastern Indonesia Time)' },
  { value: 'NZST', label: 'NZST (New Zealand Standard Time)' },
  { value: 'NZDT', label: 'NZDT (New Zealand Daylight Time)' },
];

// Surfaces the Pre-Sales POCs (1-4) and the company generic number at the top
// of the Post-Sales edit form so the BDA has every contact channel in one
// place before working the record. POC details take visual priority; the
// company generic number is shown last as a fallback.
const PreSalesContactPanel = ({ lead }) => {
  if (!lead) return null;

  const pocs = [
    { n: 1, name: lead.poc_name, email: lead.poc_email || lead.email, phone: lead.poc_contact_no || lead.contact_no, designation: lead.designation },
    { n: 2, name: lead.poc2_name, email: lead.poc2_email, phone: lead.poc2_contact_no, designation: lead.poc2_designation },
    { n: 3, name: lead.poc3_name, email: lead.poc3_email, phone: lead.poc3_contact_no, designation: lead.poc3_designation },
    { n: 4, name: lead.poc4_name, email: lead.poc4_email, phone: lead.poc4_contact_no, designation: lead.poc4_designation },
  ].filter(p => (p.name && p.name !== '-') || p.email || p.phone);

  const companyNumber = lead.company_generic_number;

  if (pocs.length === 0 && !companyNumber) return null;

  return (
    <div className="mb-3 p-3 bg-amber-50 rounded border border-amber-200">
      <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">
        Pre-Sales Contact Reference
      </div>
      {pocs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          {pocs.map(p => (
            <div key={p.n} className="bg-white rounded border border-amber-100 p-2 text-xs">
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] uppercase font-semibold text-amber-700">POC {p.n}</span>
                {p.name && p.name !== '-' && (
                  <span className="font-semibold text-gray-900">{p.name}</span>
                )}
                {p.designation && p.designation !== '-' && (
                  <span className="text-gray-500">· {p.designation}</span>
                )}
              </div>
              {p.email && (
                <div className="flex items-center gap-1 mt-1">
                  <Mail size={12} className="text-amber-600" />
                  <a href={`mailto:${p.email}`} className="text-blue-700 break-all hover:underline">{p.email}</a>
                </div>
              )}
              {p.phone && (
                <div className="flex items-center gap-1 mt-0.5">
                  <Phone size={12} className="text-amber-600" />
                  <a href={`tel:${p.phone}`} className="text-blue-700 hover:underline">{p.phone}</a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {companyNumber && (
        <div className="flex items-center gap-1 text-xs text-gray-700">
          <Phone size={12} className="text-gray-500" />
          <span className="text-gray-500">Company generic number:</span>
          <a href={`tel:${companyNumber}`} className="font-medium text-gray-900 hover:underline">{companyNumber}</a>
        </div>
      )}
    </div>
  );
};

const PostSales = ({
  onRefresh, 
  onConvertToPotentialClient, 
  onNavigateToPreSales,
  onExportExcel,
  onExportPDF,
  showExport = false,
  selectedBDE = 'All',
  selectedStage = 'All',
  selectedPriority = 'All',
  selectedDate = '',
  dateFrom: dateFromProp = '',
  dateTo: dateToProp = '',
  searchTerm: searchTermProp = '',
  reopenLead = null,
  onClearReopenLead
}) => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [postSalesRecords, setPostSalesRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [isInPotentialClients, setIsInPotentialClients] = useState(false);
  const [filterStage, setFilterStage] = useState('All');
  const [searchTerm, setSearchTerm] = useState(searchTermProp);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [dateFrom, setDateFrom] = useState(dateFromProp);
  const [dateTo, setDateTo] = useState(dateToProp);
  const [bdeUsers, setBdeUsers] = useState([]);
  // BDE record of the currently logged-in user — the one who will be recorded
  // as the post-sales handler on new saves. Any active BDE can now own a record;
  // this is no longer hard-coded to Surabhi / BDE id 12.
  const [postSalesBDE, setPostSalesBDE] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const hasLoadedRef = useRef(false);
  
  // Toggle group expansion
  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  // Helper function to normalize URLs (fix Http/Https to http/https)
  const normalizeUrl = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    // Check if URL starts with http (case-insensitive)
    if (/^https?:\/\//i.test(trimmed)) {
      // Replace the protocol with correct case
      return trimmed.replace(/^https?:\/\//i, trimmed.toLowerCase().startsWith('https') ? 'https://' : 'http://');
    }
    // No protocol, add https://
    return `https://${trimmed}`;
  };
  
  const [formData, setFormData] = useState({
    lead_id: '',
    contact_number: '',
    time_zone: '',
    call_status: '',
    email_type: '',
    email: '',
    stage: 'followup',
    notes: '',
    follow_ups: [
      { date: '', note: '' } // Follow-up 1 (always visible)
    ]
  });

  // Sync props with local state when they change
  useEffect(() => {
    setSearchTerm(searchTermProp);
    setDateFrom(dateFromProp);
    setDateTo(dateToProp);
  }, [searchTermProp, dateFromProp, dateToProp]);

  // Fetch all leads and post sales records
  // Normalize to YYYY-MM-DD for API (post-sales date filter = when post-sales was worked on)
  const toYYYYMMDD = (val) => {
    if (!val || typeof val !== 'string') return '';
    const s = val.trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
    return s;
  };

  const fetchData = useCallback(async () => {
    try {
      // Only show spinner on first load — subsequent fetches (refresh after a
      // save) just update the array silently so the user doesn't see a flash.
      if (!hasLoadedRef.current) setLoading(true);
      setLoadError(null);

      // No filter params — Pre-Sales / Post-Sales explicitly switched to
      // client-side filtering (the user said "don't keep loading every
      // time"). Filters are applied to the cached array via the filteredLeads
      // memo below. Re-fetch only fires on initial mount + post-write
      // (window.postSalesRefresh).
      const [leadsResponse, postSalesResponse] = await Promise.all([
        dashboardAPI.getPostSalesLeads({}),
        dashboardAPI.getPostSales()
      ]);

      // Support both { data: [] } and raw array from API
      const leadsList = Array.isArray(leadsResponse)
        ? leadsResponse
        : (leadsResponse?.data ?? []);
      setLeads(Array.isArray(leadsList) ? leadsList : []);

      const records = Array.isArray(postSalesResponse)
        ? postSalesResponse
        : (postSalesResponse?.success ? (postSalesResponse?.data ?? []) : []);
      setPostSalesRecords(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      const msg = error.response?.data?.error || error.message;
      const isTimeout = error.code === 'ECONNABORTED' || msg?.toLowerCase?.().includes('timeout');
      const isNetworkError = !error.response && (msg === 'fetch failed' || msg?.toLowerCase?.().includes('network'));
      const apiUrl = typeof getApiUrlForError === 'function' ? getApiUrlForError() : '';
      const fullMsg = isTimeout
        ? `Request timed out. API URL: ${apiUrl || 'unknown'}. Check that the backend is running and reachable.`
        : isNetworkError
          ? `Cannot reach the server. API URL: ${apiUrl || 'unknown'}. Locally: run \`npm run dev\` in the backend folder (default port 8000). When deployed: set REACT_APP_API_URL to your backend URL.`
          : `Error loading data: ${msg}`;
      setLoadError(fullMsg);
      alert(fullMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch once on mount; never re-fetch on filter change (filters are
  // applied client-side via filteredLeads). The parent App.js calls
  // window.postSalesRefresh after a save, which we hook up below.
  useEffect(() => {
    if (!hasLoadedRef.current) {
      fetchData();
      hasLoadedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load countries list (same as LeadModal)
  useEffect(() => {
    setAvailableCountries(Country.getAllCountries());
  }, []);

  // Fetch BDE users and resolve the logged-in user's BDE record so any BDE (Surabhi,
  // Janvi, Kima, Shiva, …) can be recorded as the post-sales handler on new saves.
  useEffect(() => {
    const fetchBDEUsers = async () => {
      try {
        const response = await dashboardAPI.getBDEUsers();
        if (response && response.data) {
          setBdeUsers(response.data);

          const emailLower = (user?.email || '').toLowerCase().trim();
          const nameLower = (user?.name || '').toLowerCase().trim();
          const userFirstName = nameLower.split(' ')[0];

          let matchedBDE = null;
          if (emailLower) {
            matchedBDE = response.data.find(b => (b.email || '').toLowerCase().trim() === emailLower);
          }
          if (!matchedBDE && nameLower) {
            matchedBDE = response.data.find(b => (b.name || '').toLowerCase().trim() === nameLower);
          }
          if (!matchedBDE && userFirstName) {
            matchedBDE = response.data.find(b => {
              const bn = (b.name || '').toLowerCase().trim();
              return bn.split(' ')[0] === userFirstName;
            });
          }

          if (matchedBDE) {
            setPostSalesBDE(matchedBDE);
            console.log('Post Sales handler resolved to logged-in user:', matchedBDE.name, 'ID:', matchedBDE.id);
          } else {
            // No matching BDE for the current user. Leave handler unset; handleSave
            // will surface an error rather than silently writing under someone else.
            setPostSalesBDE(null);
            console.warn('No BDE match for logged-in user in Post Sales:', user?.name, user?.email);
          }
        }
      } catch (error) {
        console.error('Error fetching BDE users:', error);
      }
    };
    fetchBDEUsers();
  }, [user]);

  // Expose fetchData to parent component for refresh functionality
  useEffect(() => {
    // Store the fetch function so parent can call it
    window.postSalesRefresh = fetchData;
    // Expose filter values for export
    window.postSalesFilters = {
      searchTerm,
      dateFrom,
      dateTo
    };
    return () => {
      delete window.postSalesRefresh;
      delete window.postSalesFilters;
    };
    // Only update when fetchData changes (which happens when filters change)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]);

  // Get post sales record for a lead
  const getPostSalesForLead = (leadId) => {
    return postSalesRecords.find(ps => ps.lead_id === leadId);
  };

  // Check if lead is already in potential clients
  useEffect(() => {
    const checkPotentialClient = async () => {
      if (editingLead && editingLead.id) {
        try {
          const result = await dashboardAPI.checkLeadInPotentialClients(editingLead.id);
          const isInPC = result.isInPotentialClients === true || (result.data && result.data.id) ? true : false;
          setIsInPotentialClients(isInPC);
        } catch (error) {
          console.error('Error checking potential client:', error);
          // Don't reset to false on error - keep current state
        }
      } else if (!editingLead || !editingLead.id) {
        setIsInPotentialClients(false);
      }
    };
    
    if (editingLead?.id) {
      checkPotentialClient();
    } else {
      setIsInPotentialClients(false);
    }
  }, [editingLead?.id]); // Only depend on editingLead.id

  // Handle edit
  const handleEdit = (lead) => {
    console.log('handleEdit called with lead:', lead);
    if (!lead || !lead.id) {
      console.error('Invalid lead passed to handleEdit:', lead);
      return;
    }
    const existingRecord = getPostSalesForLead(lead.id);
    console.log('Existing post sales record:', existingRecord);
    setEditingLead(lead);
    
    // Load follow-ups from existing record
    const followUps = [];
    
    // Format date from database (YYYY-MM-DD) to HTML date input format
    const formatDateForInput = (dateStr) => {
      if (!dateStr) return '';
      // If date is already in YYYY-MM-DD format, return as is
      if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
        return dateStr.split('T')[0]; // Remove time part if present
      }
      // Try to parse and format
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      } catch (e) {
        console.error('Error formatting date:', dateStr, e);
      }
      return '';
    };
    
    // Always show at least follow-up 1
    followUps.push({ 
      date: formatDateForInput(existingRecord?.follow_up_1_date), 
      note: existingRecord?.follow_up_1_note || '' 
    });
    
    // Add follow-ups 2-4 if they have data
    if (existingRecord?.follow_up_2_date || existingRecord?.follow_up_2_note) {
      followUps.push({ 
        date: formatDateForInput(existingRecord.follow_up_2_date), 
        note: existingRecord.follow_up_2_note || '' 
      });
    }
    if (existingRecord?.follow_up_3_date || existingRecord?.follow_up_3_note) {
      followUps.push({ 
        date: formatDateForInput(existingRecord.follow_up_3_date), 
        note: existingRecord.follow_up_3_note || '' 
      });
    }
    if (existingRecord?.follow_up_4_date || existingRecord?.follow_up_4_note) {
      followUps.push({ 
        date: formatDateForInput(existingRecord.follow_up_4_date), 
        note: existingRecord.follow_up_4_note || '' 
      });
    }
    
    setFormData({
      lead_id: lead.id,
      contact_number: existingRecord?.contact_number || '',
      time_zone: existingRecord?.time_zone || '',
      call_status: existingRecord?.call_status || '',
      email_type: existingRecord?.email_type || '',
      email: existingRecord?.email || '',
      stage: existingRecord?.stage || 'followup',
      notes: existingRecord?.notes || '',
      follow_ups: followUps.length > 0 ? followUps : [{ date: '', note: '' }]
    });
  };

  // When returning from pre-sales form (Back), reopen this post-sales form
  useEffect(() => {
    if (reopenLead && onClearReopenLead) {
      handleEdit(reopenLead);
      onClearReopenLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reopenLead]);

  // Handle save
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Preserve the original handler when editing an existing record — you shouldn't
      // accidentally re-assign someone else's post-sales entry just by opening it.
      const existingRecord = getPostSalesForLead(formData.lead_id);
      let handlerId = existingRecord?.post_sales_bde_id ?? postSalesBDE?.id ?? null;
      let handlerName = existingRecord?.post_sales_bde_name ?? postSalesBDE?.name ?? null;

      if (!handlerId || !handlerName) {
        alert('Could not determine the Post Sales handler from your account. Please ask an admin to link your login to a BDE record before saving.');
        setLoading(false);
        return;
      }

      const formDataWithBDE = {
        ...formData,
        post_sales_bde_id: handlerId,
        post_sales_bde_name: handlerName
      };
      
      console.log('💾 Saving post-sales formData:', JSON.stringify(formDataWithBDE, null, 2));
      console.log('📅 Follow-ups being sent:', formData.follow_ups);
      console.log('👤 Post Sales BDE:', postSalesBDE);
      const result = await dashboardAPI.createOrUpdatePostSales(formDataWithBDE);
      console.log('✅ Save result:', result);
      
      // Refresh data before closing form to ensure latest data is loaded
      await fetchData();
      
      // Also refresh follow-ups tab if it exists (since we just saved follow-up data)
      // Add a delay to ensure backend has processed the update
      setTimeout(() => {
        console.log('🔄 Attempting to refresh follow-ups tab...');
        console.log('window.followUpsRefresh available?', typeof window.followUpsRefresh);
        if (window.followUpsRefresh) {
          console.log('🔄 Calling window.followUpsRefresh()');
          window.followUpsRefresh();
        } else {
          console.warn('⚠️ window.followUpsRefresh is not available - follow-ups tab may not refresh');
        }
      }, 1000);
      
      // Refresh other components that might be affected
      if (window.potentialClientsRefresh) {
        window.potentialClientsRefresh();
      }
      if (window.activityRefresh) {
        window.activityRefresh();
      }
      if (window.highlightsRefresh) {
        window.highlightsRefresh();
      }
      
      setEditingLead(null);
      setFormData({
        lead_id: '',
        contact_number: '',
        time_zone: '',
        call_status: '',
        email_type: '',
        email: '',
        stage: 'followup',
        notes: '',
        follow_ups: [{ date: '', note: '' }]
      });
    } catch (error) {
      console.error('Error saving post sales:', error);
      alert('Error saving: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle stage update
  const handleStageUpdate = async (postSalesId, newStage) => {
    try {
      setLoading(true);
      await dashboardAPI.updatePostSalesStage(postSalesId, newStage);
      fetchData();
    } catch (error) {
      console.error('Error updating stage:', error);
      alert('Error updating stage: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle convert to potential client
  const handleConvertToPotentialClient = async (lead_id) => {
    if (!onConvertToPotentialClient) return { success: false };
    
    try {
      setLoading(true);
      const result = await onConvertToPotentialClient(lead_id);
      if (result && result.success) {
        // Don't close the form - let the button disappear
        // Just update the state to hide the button
        setIsInPotentialClients(true);
        // Refresh data but keep form open
        fetchData();
        return result;
      } else {
        const errorMsg = result?.error || result?.message || 'Failed to add to potential clients';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Error converting to potential client:', error);
      console.error('Full error object:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      let errorMessage = 'Unknown error';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.toString) {
        errorMessage = error.toString();
      }
      
      // Show detailed error
      alert(`Error adding to potential clients:\n\n${errorMessage}\n\nDetails: ${error.response?.data?.details || 'No additional details'}\n\nPlease check:\n1. Backend server is running on port 5001\n2. potential_clients table exists in database\n3. Check browser console (F12) for more details`);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Get stage badge color
  const getStageBadgeColor = (stage) => {
    switch (stage) {
      case 'done':
        return 'bg-green-100 text-green-800';
      case 'followup':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get call status badge color
  const getCallStatusBadgeColor = (status) => {
    switch (status) {
      case 'answered':
        return 'bg-green-100 text-green-800';
      case 'not answered':
        return 'bg-red-100 text-red-800';
      case 'voicemail':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get email type badge color
  const getEmailTypeBadgeColor = (type) => {
    switch (type) {
      case 'personal':
        return 'bg-blue-100 text-blue-800';
      case 'provided':
        return 'bg-green-100 text-green-800';
      case 'generic':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // FULLY client-side filter — Pre-Sales / Post-Sales no longer hit the
  // backend on filter change. Everything below runs against the in-memory
  // `leads` array cached on first load.
  const POST_SALES_STAGES_LIST = ['done', 'in_progress', 'followup', 'pending'];
  const fromVal = toYYYYMMDD(dateFrom);
  const toVal = toYYYYMMDD(dateTo);
  const singleDate = toYYYYMMDD(selectedDate);
  const q = (searchTerm || '').trim().toLowerCase();

  // Convert a value to the user's LOCAL calendar day (YYYY-MM-DD).
  // - post_sales_updated_at is a UTC timestamp (has a "T"): convert to the local
  //   day so work done late evening IST isn't counted on the previous UTC day —
  //   that mismatch made date filters miss leads.
  // - lead_date is already a calendar day (no "T"): use it verbatim (converting a
  //   bare date through Date() would shift it across the UTC boundary).
  const toLocalDay = (val) => {
    if (typeof val !== 'string' || !val) return '';
    if (!val.includes('T')) return val.slice(0, 10);
    const d = new Date(val);
    if (isNaN(d.getTime())) return val.slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Authoritative post-sales stage for a lead. Prefer the enriched field the
  // backend computes per lead (getPostSalesLeads — batched, never truncated);
  // fall back to the records array only if it's missing.
  const stageOfLead = (lead) => {
    if (lead.post_sales_stage) return lead.post_sales_stage;
    return getPostSalesForLead(lead.id)?.stage || null;
  };

  // Memoized so the full O(leads) filter (and the groupedLeads memo that depends
  // on it) only re-runs when the data or a filter actually changes — NOT on every
  // render. Clicking a company name (which sets editingLead / expandedGroups) was
  // forcing this whole pipeline to recompute on a large dataset, which is the most
  // likely cause of the Post Sales freeze. Logic below is byte-for-byte unchanged.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredLeads = useMemo(() => leads.filter(lead => {
    // Internal stage filter (from this tab's own UI)
    if (filterStage !== 'All') {
      if (stageOfLead(lead) !== filterStage) return false;
    }
    // External Stage filter from FilterBar
    if (selectedStage !== 'All') {
      if (POST_SALES_STAGES_LIST.includes(selectedStage)) {
        if (stageOfLead(lead) !== selectedStage) return false;
      } else if ((lead.stage || '') !== selectedStage) {
        return false;
      }
    }
    // BDE filter — match ONLY the post-sales worker (post_sales_bde_name), which
    // is exactly what the BDE column shows. Post Sales is independent of the
    // pre-sales tab, so the pre-sales owner (lead.bde_name) is intentionally NOT
    // used here — otherwise filtering by one BDE could surface rows another BDE
    // actually worked in post-sales.
    if (selectedBDE !== 'All' && (lead.post_sales_bde_name || '') !== selectedBDE) return false;
    // Priority filter
    if (selectedPriority !== 'All' && (lead.priority || '') !== selectedPriority) return false;
    // Date filters — STRICTLY match the post-sales WORK day (when the post-sales
    // record was last updated), in the user's local timezone. This is purely a
    // post-sales signal: the pre-sales lead_date is never used, so a date filter
    // shows exactly the records that were worked in post-sales on those days. A
    // lead with no post-sales work is excluded whenever a date filter is active.
    const hasDateFilter = !!(singleDate || fromVal || toVal);
    if (hasDateFilter) {
      const psDate = (typeof lead.post_sales_updated_at === 'string' && lead.post_sales_updated_at)
        ? toLocalDay(lead.post_sales_updated_at)
        : '';
      if (!psDate) return false; // never worked in post-sales → not a match for a date filter
      if (singleDate && psDate !== singleDate) return false;
      if (fromVal && psDate < fromVal) return false;
      if (toVal   && psDate > toVal)   return false;
    }
    // Search — company, POC, email
    if (q) {
      const c = (lead.company_name || '').toLowerCase();
      const p = (lead.poc_name || '').toLowerCase();
      const e = (lead.email || lead.poc_email || '').toLowerCase();
      if (!c.includes(q) && !p.includes(q) && !e.includes(q)) return false;
    }
    return true;
  }), [leads, postSalesRecords, filterStage, selectedStage, selectedBDE, selectedPriority, fromVal, toVal, singleDate, q]);

  // Group leads by company_group_id (same logic as LeadsTable)
  const groupedLeads = useMemo(() => {
    const groups = new Map();
    const ungrouped = [];
    
    // Normalize company name for comparison
    const normalizeName = (name) => {
      if (!name) return '';
      return name
        .toLowerCase()
        .trim()
        .replace(/\s*-\s*solar\s*$/i, '')
        .replace(/\s*-\s*wind\s*$/i, '')
        .replace(/\s+(UK|USA|US|GmbH|Inc|LLC|Ltd|Corp|Co\.|A\/S|AG|Group)\s*$/i, '')
        .trim();
    };
    
    // Normalize location for comparison
    const normalizeLocation = (city, country) => {
      const cityNorm = (city || '').toLowerCase().trim();
      const countryNorm = (country || '').toLowerCase().trim();
      return `${cityNorm}::${countryNorm}`;
    };
    
    // First, group by company_group_id
    const tempGroups = new Map();
    filteredLeads.forEach(lead => {
      const groupId = lead.company_group_id;
      
      if (groupId) {
        if (!tempGroups.has(groupId)) {
          tempGroups.set(groupId, []);
        }
        tempGroups.get(groupId).push(lead);
      } else {
        ungrouped.push(lead);
      }
    });
    
    // Process groups
    tempGroups.forEach((group, groupId) => {
      // Filter out leads with completely null locations
      const validLeads = group.filter(lead => {
        const hasCity = lead.city && lead.city.trim() && lead.city !== '-';
        const hasCountry = lead.country && lead.country.trim() && lead.country !== '-';
        return hasCity || hasCountry;
      });
      
      // If no leads have location, still process the full group so they show up
      const leadsToProcess = validLeads.length > 0 ? validLeads : group;
      if (leadsToProcess.length === 0) {
        return;
      }
      
      // Group by normalized name + country
      const countryGroups = new Map();
      leadsToProcess.forEach(lead => {
        const normalizedName = normalizeName(lead.company_name);
        const country = (lead.country || '').toLowerCase().trim() || 'unknown';
        const key = `${normalizedName}::${country}`;
        
        if (!countryGroups.has(key)) {
          countryGroups.set(key, []);
        }
        countryGroups.get(key).push(lead);
      });
      
      // Merge leads with same country
      const mergedByCountry = [];
      countryGroups.forEach((countryGroup) => {
        if (countryGroup.length === 1) {
          mergedByCountry.push(countryGroup[0]);
        } else {
          // Multiple leads with same country - prefer ones with city data
          const withCity = countryGroup.filter(l => l.city && l.city.trim() && l.city !== '-');
          const withoutCity = countryGroup.filter(l => !l.city || !l.city.trim() || l.city === '-');
          const leadsToMerge = withCity.length > 0 ? withCity : withoutCity;
          
          // Group by full location
          const locationGroups = new Map();
          leadsToMerge.forEach(lead => {
            const location = normalizeLocation(lead.city, lead.country);
            const locationKey = `${normalizeName(lead.company_name)}::${location}`;
            
            if (!locationGroups.has(locationKey)) {
              locationGroups.set(locationKey, []);
            }
            locationGroups.get(locationKey).push(lead);
          });
          
          // For each location group, keep only the best lead
          locationGroups.forEach((locationGroup) => {
            if (locationGroup.length > 1) {
              // Multiple leads in same location - merge them
              const bestLead = locationGroup.reduce((best, current) => {
                const stagePriority = { 'Proposal': 3, 'Contacted': 2, 'New': 1 };
                const priorityPriority = { 'High': 3, 'Medium': 2, 'Low': 1 };
                const bestStage = stagePriority[best.stage] || 0;
                const currentStage = stagePriority[current.stage] || 0;
                if (currentStage !== bestStage) return currentStage > bestStage ? current : best;
                const bestPrio = priorityPriority[best.priority] || 0;
                const currentPrio = priorityPriority[current.priority] || 0;
                if (currentPrio !== bestPrio) return currentPrio > bestPrio ? current : best;
                return new Date(current.lead_date) > new Date(best.lead_date) ? current : best;
              });
              mergedByCountry.push(bestLead);
            } else {
              mergedByCountry.push(locationGroup[0]);
            }
          });
        }
      });
      
      // If multiple locations, create a group
      if (mergedByCountry.length > 1) {
        const mostRecentLead = mergedByCountry.reduce((best, current) => 
          new Date(current.lead_date) > new Date(best.lead_date) ? current : best
        );
        const bestStageLead = mergedByCountry.reduce((best, current) => {
          const stagePriority = { 'Proposal': 3, 'Contacted': 2, 'New': 1 };
          return (stagePriority[current.stage] || 0) > (stagePriority[best.stage] || 0) ? current : best;
        });
        const leadWithPOC = mergedByCountry.find(l => l.poc_name && l.poc_name !== '-') || mergedByCountry[0];

        // Pick location from the SAME lead (never mix city from one and country from another)
        const hasValidCity = (l) => l.city && l.city.trim() && l.city !== '-';
        const hasValidCountry = (l) => l.country && l.country.trim() && l.country !== '-';
        const locationLead =
          (hasValidCity(mostRecentLead) && hasValidCountry(mostRecentLead) ? mostRecentLead : null) ||
          mergedByCountry.find(l => hasValidCity(l) && hasValidCountry(l)) ||
          mergedByCountry.find(l => hasValidCountry(l)) ||
          mostRecentLead;

        groups.set(groupId, {
          isGroup: true,
          groupId: groupId,
          leads: mergedByCountry,
          lead_date: mostRecentLead.lead_date,
          company_name: mergedByCountry[0].company_name.replace(/\s+(UK|USA|US|GmbH|Inc|LLC|Ltd|Corp)\s*$/i, '').trim(),
          stage: bestStageLead.stage,
          priority: bestStageLead.priority,
          bde_name: mostRecentLead.bde_name,
          poc_name: leadWithPOC.poc_name || '-',
          email: leadWithPOC.email || leadWithPOC.poc_email || '-',
          contact_no: leadWithPOC.poc_contact_no || leadWithPOC.contact_no || '-',
          city: locationLead.city || '-',
          country: locationLead.country || '-',
          locations: mergedByCountry.map(l => ({
            id: l.id,
            name: l.company_name,
            city: l.city,
            country: l.country,
            poc_name: l.poc_name,
            email: l.email || l.poc_email,
            contact_no: l.poc_contact_no || l.contact_no,
            stage: l.stage,
            priority: l.priority,
            lead_date: l.lead_date,
            bde_name: l.bde_name,
            company_url: l.company_url,
            linkedin_url: l.linkedin_url
          }))
        });
      } else {
        ungrouped.push(mergedByCountry[0]);
      }
    });
    
    // Also merge ungrouped leads that have duplicates with "- Solar" suffix in same location
    // First, filter out leads with completely null locations
    const validUngrouped = ungrouped.filter(lead => {
      const hasCity = lead.city && lead.city.trim() && lead.city !== '-';
      const hasCountry = lead.country && lead.country.trim() && lead.country !== '-';
      return hasCity || hasCountry; // Keep if at least one location field exists
    });
    
    // Group ungrouped leads by normalized name + country (to handle same country with/without city)
    const ungroupedCountryGroups = new Map();
    validUngrouped.forEach(lead => {
      const normalizedName = normalizeName(lead.company_name);
      const country = (lead.country || '').toLowerCase().trim() || 'unknown';
      const key = `${normalizedName}::${country}`;
      
      if (!ungroupedCountryGroups.has(key)) {
        ungroupedCountryGroups.set(key, []);
      }
      ungroupedCountryGroups.get(key).push(lead);
    });
    
    // Merge duplicates in ungrouped leads
    const mergedUngrouped = [];
    ungroupedCountryGroups.forEach((countryGroup) => {
      if (countryGroup.length === 1) {
        mergedUngrouped.push(countryGroup[0]);
      } else {
        // Multiple leads with same country - prefer ones with city data
        const withCity = countryGroup.filter(l => l.city && l.city.trim() && l.city !== '-');
        const withoutCity = countryGroup.filter(l => !l.city || !l.city.trim() || l.city === '-');
        
        // If there are leads with city, use only those (and merge among them)
        // Otherwise, use leads without city
        const leadsToMerge = withCity.length > 0 ? withCity : withoutCity;
        
        // Group by full location (city + country)
        const locationGroups = new Map();
        leadsToMerge.forEach(lead => {
          const location = normalizeLocation(lead.city, lead.country);
          const locationKey = `${normalizeName(lead.company_name)}::${location}`;
          
          if (!locationGroups.has(locationKey)) {
            locationGroups.set(locationKey, []);
          }
          locationGroups.get(locationKey).push(lead);
        });
        
        // For each location group, keep only the best lead
        locationGroups.forEach((locationGroup) => {
          if (locationGroup.length > 1) {
            // Multiple leads in same location - merge them
            const bestLead = locationGroup.reduce((best, current) => {
              const stagePriority = { 'Proposal': 3, 'Contacted': 2, 'New': 1 };
              const currentStage = stagePriority[current.stage] || 0;
              const bestStage = stagePriority[best.stage] || 0;
              
              if (currentStage > bestStage) return current;
              if (currentStage === bestStage) {
                const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
                const currentPriority = priorityOrder[current.priority] || 0;
                const bestPriority = priorityOrder[best.priority] || 0;
                if (currentPriority > bestPriority) return current;
                // Prefer the one without "- Solar" suffix
                if (currentPriority === bestPriority) {
                  const currentHasSolar = /\s*-\s*solar\s*$/i.test(current.company_name);
                  const bestHasSolar = /\s*-\s*solar\s*$/i.test(best.company_name);
                  if (bestHasSolar && !currentHasSolar) return current;
                }
              }
              return best;
            }, locationGroup[0]);
            mergedUngrouped.push(bestLead);
          } else {
            mergedUngrouped.push(locationGroup[0]);
          }
        });
      }
    });
    
    // Convert groups to array and combine with merged ungrouped (same as LeadsTable)
    const groupedArray = Array.from(groups.values());
    
    // Sort both groups and ungrouped by lead_date (latest to oldest) to match Pre Sales order
    groupedArray.sort((a, b) => {
      const dateA = new Date(a.lead_date || 0);
      const dateB = new Date(b.lead_date || 0);
      return dateB - dateA; // Descending order (newest first)
    });
    
    mergedUngrouped.sort((a, b) => {
      const dateA = new Date(a.lead_date || 0);
      const dateB = new Date(b.lead_date || 0);
      return dateB - dateA; // Descending order (newest first)
    });
    
    // Combine groups and ungrouped, then sort the final array by lead_date (latest to oldest)
    const combined = [...groupedArray, ...mergedUngrouped.map(lead => ({ isGroup: false, lead }))];
    
    // Final sort to ensure all items are ordered by lead_date (latest to oldest) - same as Pre Sales
    combined.sort((a, b) => {
      const dateA = new Date((a.isGroup ? a.lead_date : a.lead?.lead_date) || 0);
      const dateB = new Date((b.isGroup ? b.lead_date : b.lead?.lead_date) || 0);
      return dateB - dateA; // Descending order (newest first)
    });
    
    return combined;
  }, [filteredLeads]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Post Sales ({groupedLeads.length})</h2>
        <div className="flex gap-4">
          <select
            value={filterStage}
            onChange={(e) => setFilterStage(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
          >
            <option value="All">All Stages</option>
            <option value="done">Done</option>
            <option value="followup">Follow-up</option>
            <option value="in_progress">In Progress</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {loading && !editingLead ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#679632]"></div>
          <p className="mt-2 text-gray-500">Loading post sales data...</p>
        </div>
      ) : loadError ? (
        <div className="text-center py-8 px-4 bg-amber-50 border border-amber-200 rounded-lg max-w-2xl mx-auto">
          <p className="text-amber-800 text-sm mb-4">{loadError}</p>
          <button
            type="button"
            onClick={() => { setLoadError(null); fetchData(); }}
            className="px-4 py-2 bg-[#679632] text-white rounded hover:bg-[#587f2b]"
          >
            Retry
          </button>
        </div>
      ) : groupedLeads.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No leads found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <table className="min-w-full">
              <thead className="bg-white border-t border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    POC Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    URLs
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Call Status / Email Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Stage
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    BDE
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {groupedLeads
                  .map((item, index) => {
                    // Handle grouped vs ungrouped leads
                    const lead = item.isGroup ? null : item.lead;
                    const isGroup = item.isGroup;
                    
                    if (isGroup) {
                      const isExpanded = expandedGroups.has(item.groupId);
                      const locationsCount = item.locations.length;
                      
                      return (
                        <React.Fragment key={item.groupId}>
                          <tr 
                            className="hover:bg-gray-50 cursor-pointer border-l-2 border-[#679632]"
                            onClick={() => toggleGroup(item.groupId)}
                          >
                            <td className="px-2 py-1.5 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {index + 1}
                              </div>
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900 flex-1">{item.company_name}</span>
                                <span className="text-xs text-gray-500 flex-shrink-0">({locationsCount} divisions)</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleGroup(item.groupId);
                                  }}
                                  className="flex items-center justify-center w-7 h-7 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                                  title={isExpanded ? 'Collapse' : 'Expand'}
                                >
                                  {isExpanded ? <ChevronUp size={18} className="text-gray-700" /> : <ChevronDown size={18} className="text-gray-700" />}
                                </button>
                              </div>
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="text-sm text-gray-700">
                                {(() => {
                                  if (!item.country) return <span className="text-gray-400">-</span>;
                                  const countryName = item.country.trim();
                                  let countryMatch = availableCountries.find(c => c.name === countryName);
                                  if (!countryMatch) {
                                    countryMatch = availableCountries.find(c => c.name.toLowerCase() === countryName.toLowerCase());
                                  }
                                  return countryMatch ? countryMatch.name : countryName;
                                })()}
                              </div>
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="text-sm text-gray-900 space-y-1">
                                {/* Get POC info from first lead with POC data */}
                                {(() => {
                                  const hasAnyContact = (l) => l && (
                                    (l.poc_name && l.poc_name !== '-') ||
                                    l.email || l.poc_email ||
                                    l.contact_no || l.poc_contact_no ||
                                    l.poc2_name || l.poc3_name || l.poc4_name ||
                                    l.company_generic_number
                                  );
                                  const leadWithPOC = item.leads?.find(hasAnyContact) || item.leads?.[0];

                                  if (!hasAnyContact(leadWithPOC)) {
                                    return <span className="text-gray-400 text-sm">N/A</span>;
                                  }

                                  // POC1 in the view is bare (poc_name/email/contact_no); POC2-4 use the prefixed names.
                                  const pocEmail = leadWithPOC.email || leadWithPOC.poc_email;
                                  const pocPhone = leadWithPOC.contact_no || leadWithPOC.poc_contact_no;
                                  const extraPocCount = ['poc2_name', 'poc3_name', 'poc4_name']
                                    .filter(k => leadWithPOC[k] && String(leadWithPOC[k]).trim()).length;

                                  return (
                                    <>
                                      {leadWithPOC.poc_name && leadWithPOC.poc_name !== '-' && (
                                        <div className="font-medium text-gray-900">{leadWithPOC.poc_name}</div>
                                      )}
                                      {pocEmail && (
                                        <div className="flex items-center gap-1 text-xs">
                                          <Mail size={14} className="text-blue-500" />
                                          <span className="text-blue-600 break-all">{pocEmail}</span>
                                        </div>
                                      )}
                                      {pocPhone && (
                                        <div className="flex items-center gap-1 text-xs">
                                          <Phone size={14} className="text-blue-500" />
                                          <span className="text-blue-600">{pocPhone}</span>
                                        </div>
                                      )}
                                      {leadWithPOC.company_generic_number && (
                                        <div className="flex items-center gap-1 text-xs">
                                          <Phone size={14} className="text-gray-400" />
                                          <span className="text-gray-600" title="Company generic number">{leadWithPOC.company_generic_number}</span>
                                        </div>
                                      )}
                                      {extraPocCount > 0 && (
                                        <div className="text-[10px] text-gray-500 italic">+{extraPocCount} more POC{extraPocCount === 1 ? '' : 's'} on file</div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="px-2 py-1.5 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                {item.leads && item.leads[0]?.company_url && (
                                  <a
                                    href={normalizeUrl(item.leads[0].company_url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    <Globe size={16} />
                                  </a>
                                )}
                                {item.leads && item.leads[0]?.linkedin_url && (
                                  <a
                                    href={normalizeUrl(item.leads[0].linkedin_url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    <ExternalLink size={16} />
                                  </a>
                                )}
                                {(!item.leads || (!item.leads[0]?.company_url && !item.leads[0]?.linkedin_url)) && (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </div>
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="text-sm space-y-1">
                                {/* Get post sales record for first lead in group */}
                                {item.leads && item.leads[0] && (() => {
                                  const firstLeadPostSales = getPostSalesForLead(item.leads[0].id);
                                  return (
                                    <>
                                      {firstLeadPostSales?.call_status && (
                                        <div>
                                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCallStatusBadgeColor(firstLeadPostSales.call_status)}`}>
                                            {firstLeadPostSales.call_status}
                                          </span>
                                        </div>
                                      )}
                                      {firstLeadPostSales?.email_type && (
                                        <div>
                                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEmailTypeBadgeColor(firstLeadPostSales.email_type)}`}>
                                            {firstLeadPostSales.email_type}
                                          </span>
                                        </div>
                                      )}
                                      {firstLeadPostSales?.time_zone && (
                                        <div className="flex items-center gap-1 text-xs text-gray-600">
                                          <MapPin size={12} className="text-gray-400" />
                                          <span>{firstLeadPostSales.time_zone}</span>
                                        </div>
                                      )}
                                      {!firstLeadPostSales && <span className="text-gray-400 text-xs">-</span>}
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="px-2 py-1.5 whitespace-nowrap">
                              {item.leads && item.leads[0] && (() => {
                                const firstLeadPostSales = getPostSalesForLead(item.leads[0].id);
                                return firstLeadPostSales ? (
                                  <div className="relative">
                                    <select
                                      value={firstLeadPostSales.stage}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleStageUpdate(firstLeadPostSales.id, e.target.value);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className={`text-xs font-semibold rounded-full px-3 py-1.5 border-0 appearance-none pr-8 cursor-pointer ${getStageBadgeColor(firstLeadPostSales.stage)}`}
                                    >
                                      <option value="followup">Follow-up</option>
                                      <option value="done">Done</option>
                                      <option value="in_progress">In Progress</option>
                                      <option value="pending">Pending</option>
                                    </select>
                                    <ChevronDown size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-600" />
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                );
                              })()}
                            </td>
                            <td className="px-2 py-1.5 whitespace-nowrap">
                              {(() => {
                                const firstLeadPostSales = item.leads && item.leads[0] && getPostSalesForLead(item.leads[0].id);
                                const bdeName = firstLeadPostSales?.post_sales_bde_name;
                                return bdeName
                                  ? <span className="text-sm text-gray-900">{bdeName}</span>
                                  : <span className="text-gray-400 text-xs italic">Not assigned</span>;
                              })()}
                            </td>
                            <td className="px-2 py-1.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('Edit/Log button clicked for grouped row:', item.groupId, item.leads);
                                  if (item.leads && item.leads.length > 0) {
                                    console.log('Calling handleEdit with lead:', item.leads[0]);
                                    handleEdit(item.leads[0]);
                                  } else {
                                    console.error('No leads found in group:', item);
                                  }
                                }}
                                className={`flex items-center gap-1 text-sm font-medium ${
                                  item.leads && item.leads[0] && getPostSalesForLead(item.leads[0].id)
                                    ? 'text-red-600 hover:text-red-700'
                                    : 'text-green-600 hover:text-green-700'
                                }`}
                              >
                                {item.leads && item.leads[0] && getPostSalesForLead(item.leads[0].id) ? (
                                  <>
                                    <Edit size={14} />
                                    <span>Edit</span>
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle size={14} />
                                    <span>Log</span>
                                  </>
                                )}
                              </button>
                            </td>
                          </tr>
                          {/* Show form if parent row lead is being edited (first lead in group) */}
                          {editingLead && item.leads && item.leads[0] && item.leads[0].id === editingLead.id && !isExpanded && (
                            <tr className="bg-green-50">
                              <td colSpan="9" className="px-4 py-4">
                                {/* Render the same form as ungrouped leads - we'll reuse the form from below */}
                                {/* For now, we'll render it inline here */}
                                <div className="text-sm text-gray-600">
                                  Form will render here for parent row
                                </div>
                              </td>
                            </tr>
                          )}
                          {isExpanded && item.locations.map((location) => {
                            const locationLead = filteredLeads.find(l => l.id === location.id);
                            return (
                              <React.Fragment key={`${item.groupId}-${location.id}`}>
                              <tr 
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => locationLead && handleEdit(locationLead)}
                              >
                                <td className="px-2 py-1.5 whitespace-nowrap pl-8">
                                  <div className="text-sm text-gray-500">
                                    {location.lead_date ? new Date(location.lead_date + 'T00:00:00').toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit'
                                    }) : '-'}
                                  </div>
                                </td>
                                <td className="px-2 py-1.5 pl-8">
                                  <div className="text-sm text-gray-700">{location.name}</div>
                                </td>
                                <td className="px-2 py-1.5 pl-8">
                                  <div className="text-sm text-gray-700">
                                    {(() => {
                                      if (!location.country) return <span className="text-gray-400">-</span>;
                                      const countryName = location.country.trim();
                                      let countryMatch = availableCountries.find(c => c.name === countryName);
                                      if (!countryMatch) {
                                        countryMatch = availableCountries.find(c => c.name.toLowerCase() === countryName.toLowerCase());
                                      }
                                      return countryMatch ? countryMatch.name : countryName;
                                    })()}
                                  </div>
                                </td>
                                <td className="px-2 py-1.5 pl-8">
                                  <div className="text-sm text-gray-900 space-y-1">
                                    {(() => {
                                      const locationPostSales = locationLead ? getPostSalesForLead(locationLead.id) : null;
                                      const preName = locationLead?.poc_name && locationLead.poc_name !== '-' ? locationLead.poc_name : '';
                                      // POC1 in sales_leads_view is bare (email/contact_no, no poc_ prefix);
                                      // older code expected `poc_email`/`poc_contact_no` and silently rendered nothing.
                                      const preEmail = locationLead?.email || locationLead?.poc_email || '';
                                      const prePhone = locationLead?.contact_no || locationLead?.poc_contact_no || '';
                                      const companyGenericNumber = locationLead?.company_generic_number || '';
                                      const extraPocCount = ['poc2_name', 'poc3_name', 'poc4_name']
                                        .filter(k => locationLead?.[k] && String(locationLead[k]).trim()).length;
                                      const psEmail = locationPostSales?.email || '';
                                      const psPhone = locationPostSales?.contact_number || '';
                                      const normalize = (v) => (v || '').trim().toLowerCase();
                                      const extraEmail = psEmail && normalize(psEmail) !== normalize(preEmail) ? psEmail : '';
                                      const extraPhone = psPhone && normalize(psPhone) !== normalize(prePhone) ? psPhone : '';

                                      if (!preName && !preEmail && !prePhone && !companyGenericNumber && !extraEmail && !extraPhone) {
                                        return <span className="text-gray-400 text-sm">N/A</span>;
                                      }

                                      return (
                                        <>
                                          {preName && (
                                            <div className="font-medium text-blue-700">{preName}</div>
                                          )}
                                          {preEmail && (
                                            <div className="flex items-center gap-1 text-xs">
                                              <Mail size={14} className="text-blue-500" />
                                              <span className="text-blue-600 break-all">{preEmail}</span>
                                            </div>
                                          )}
                                          {prePhone && (
                                            <div className="flex items-center gap-1 text-xs">
                                              <Phone size={14} className="text-blue-500" />
                                              <span className="text-blue-600">{prePhone}</span>
                                            </div>
                                          )}
                                          {companyGenericNumber && (
                                            <div className="flex items-center gap-1 text-xs">
                                              <Phone size={14} className="text-gray-400" />
                                              <span className="text-gray-600" title="Company generic number">{companyGenericNumber}</span>
                                            </div>
                                          )}
                                          {extraPocCount > 0 && (
                                            <div className="text-[10px] text-gray-500 italic">+{extraPocCount} more POC{extraPocCount === 1 ? '' : 's'} on file</div>
                                          )}
                                          {(extraEmail || extraPhone) && (
                                            <div className="mt-1 pt-1 border-t border-dashed border-purple-200 space-y-0.5">
                                              <div className="text-[10px] uppercase tracking-wide text-purple-500 font-semibold">Added in post-sales</div>
                                              {extraEmail && (
                                                <div className="flex items-center gap-1 text-xs">
                                                  <Mail size={14} className="text-purple-500" />
                                                  <span className="text-purple-700 break-all">{extraEmail}</span>
                                                </div>
                                              )}
                                              {extraPhone && (
                                                <div className="flex items-center gap-1 text-xs">
                                                  <Phone size={14} className="text-purple-500" />
                                                  <span className="text-purple-700">{extraPhone}</span>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </td>
                                <td className="px-2 py-1.5 pl-8 whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    {locationLead?.company_url && (
                                      <a
                                        href={normalizeUrl(locationLead.company_url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-blue-600 hover:text-blue-800"
                                      >
                                        <Globe size={16} />
                                      </a>
                                    )}
                                    {locationLead?.linkedin_url && (
                                      <a
                                        href={normalizeUrl(locationLead.linkedin_url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-blue-600 hover:text-blue-800"
                                      >
                                        <ExternalLink size={16} />
                                      </a>
                                    )}
                                    {(!locationLead?.company_url && !locationLead?.linkedin_url) && (
                                      <span className="text-gray-400 text-xs">-</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-2 py-1.5 pl-8">
                                  <div className="text-sm space-y-1">
                                    {(() => {
                                      const locationPostSales = locationLead ? getPostSalesForLead(locationLead.id) : null;
                                      return (
                                        <>
                                          {locationPostSales?.call_status && (
                                            <div>
                                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCallStatusBadgeColor(locationPostSales.call_status)}`}>
                                                {locationPostSales.call_status}
                                              </span>
                                            </div>
                                          )}
                                          {locationPostSales?.email_type && (
                                            <div>
                                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEmailTypeBadgeColor(locationPostSales.email_type)}`}>
                                                {locationPostSales.email_type}
                                              </span>
                                            </div>
                                          )}
                                          {locationPostSales?.time_zone && (
                                            <div className="flex items-center gap-1 text-xs text-gray-600">
                                              <MapPin size={12} className="text-gray-400" />
                                              <span>{locationPostSales.time_zone}</span>
                                            </div>
                                          )}
                                          {!locationPostSales && <span className="text-gray-400 text-xs">-</span>}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </td>
                                <td className="px-2 py-1.5 pl-8 whitespace-nowrap">
                                  {(() => {
                                    const locationPostSales = locationLead ? getPostSalesForLead(locationLead.id) : null;
                                    return locationPostSales ? (
                                      <div className="relative">
                                        <select
                                          value={locationPostSales.stage}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleStageUpdate(locationPostSales.id, e.target.value);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className={`text-xs font-semibold rounded-full px-3 py-1.5 border-0 appearance-none pr-8 cursor-pointer ${getStageBadgeColor(locationPostSales.stage)}`}
                                        >
                                          <option value="followup">Follow-up</option>
                                          <option value="done">Done</option>
                                          <option value="in_progress">In Progress</option>
                                          <option value="pending">Pending</option>
                                        </select>
                                        <ChevronDown size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-600" />
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 text-xs">-</span>
                                    );
                                  })()}
                                </td>
                                <td className="px-2 py-1.5 pl-8 whitespace-nowrap">
                                  {(() => {
                                    const locationPostSales = locationLead ? getPostSalesForLead(locationLead.id) : null;
                                    const bdeName = locationPostSales?.post_sales_bde_name;
                                    return bdeName
                                      ? <span className="text-sm text-gray-900">{bdeName}</span>
                                      : <span className="text-gray-400 text-xs italic">Not assigned</span>;
                                  })()}
                                </td>
                                <td className="px-2 py-1.5 pl-8 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('Edit/Log button clicked for division row:', location.id, locationLead);
                                      if (locationLead) {
                                        console.log('Calling handleEdit with locationLead:', locationLead);
                                        handleEdit(locationLead);
                                      } else {
                                        console.error('locationLead not found for location:', location);
                                      }
                                    }}
                                    className={`flex items-center gap-1 text-sm font-medium ${
                                      locationLead && getPostSalesForLead(locationLead.id)
                                        ? 'text-red-600 hover:text-red-700'
                                        : 'text-green-600 hover:text-green-700'
                                    }`}
                                  >
                                    {locationLead && getPostSalesForLead(locationLead.id) ? (
                                      <>
                                        <Edit size={14} />
                                        <span>Edit</span>
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle size={14} />
                                        <span>Log</span>
                                      </>
                                    )}
                                  </button>
                                </td>
                              </tr>
                              {/* Show form if this division is being edited */}
                              {editingLead && locationLead && locationLead.id === editingLead.id && (
                                <tr className="bg-green-50">
                                  <td colSpan="9" className="px-4 py-4 pl-8">
                                    <form onSubmit={handleSave} className="space-y-3">
                                      <div className="flex justify-between items-center mb-3">
                                        <h4 className="text-sm font-semibold text-gray-900">
                                          Edit Post Sales - {editingLead.company_name}
                                        </h4>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setEditingLead(null);
                                            setFormData({
                                              lead_id: '',
                                              contact_number: '',
                                              time_zone: '',
                                              call_status: '',
                                              email_type: '',
                                              email: '',
                                              stage: 'followup',
                                              notes: '',
                                              follow_ups: [{ date: '', note: '' }]
                                            });
                                          }}
                                          className="text-gray-400 hover:text-gray-600 text-sm"
                                        >
                                          ✕ Cancel
                                        </button>
                                      </div>
                                      
                                      {/* Pre-Sales Contact Reference (POCs prioritized over company generic number) */}
                                      <PreSalesContactPanel lead={editingLead} />

                                      {/* Post Sales BDE Display */}
                                      {(() => {
                                        const currentRecord = getPostSalesForLead(editingLead?.id);
                                        const bdeName = currentRecord?.post_sales_bde_name || postSalesBDE?.name || '—';
                                        return (
                                          <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs font-medium text-gray-700">Post Sales Handler:</span>
                                              <span className="text-sm font-semibold text-blue-700">{bdeName}</span>
                                              <span className="text-xs text-gray-500">(BDE ID: {currentRecord?.post_sales_bde_id || postSalesBDE?.id || '—'})</span>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                      
                                      <div className="grid grid-cols-4 gap-4">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Contact Number
                                          </label>
                                          <input
                                            type="text"
                                            value={formData.contact_number}
                                            onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                            placeholder="Enter contact number"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Time Zone
                                          </label>
                                          <select
                                            value={formData.time_zone}
                                            onChange={(e) => setFormData({ ...formData, time_zone: e.target.value })}
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                          >
                                            <option value="">Select time zone</option>
                                            {TIME_ZONES.map(tz => (
                                              <option key={tz.value} value={tz.value}>{tz.label}</option>
                                            ))}
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Call Status
                                          </label>
                                          <select
                                            value={formData.call_status}
                                            onChange={(e) => setFormData({ ...formData, call_status: e.target.value })}
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                          >
                                            <option value="">Select call status</option>
                                            <option value="answered">Answered</option>
                                            <option value="not answered">Not Answered</option>
                                            <option value="voicemail">Voicemail</option>
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Email Type
                                          </label>
                                          <select
                                            value={formData.email_type}
                                            onChange={(e) => setFormData({ ...formData, email_type: e.target.value })}
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                          >
                                            <option value="">Select email type</option>
                                            <option value="generic">Generic</option>
                                            <option value="personal">Personal</option>
                                            <option value="provided">Provided</option>
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Email
                                          </label>
                                          <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                            placeholder="Enter email address"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Stage
                                          </label>
                                          <select
                                            value={formData.stage}
                                            onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                          >
                                            <option value="followup">Follow-up</option>
                                            <option value="done">Done</option>
                                            <option value="in_progress">In Progress</option>
                                            <option value="pending">Pending</option>
                                          </select>
                                        </div>
                                      </div>
                                      
                                      {/* Follow-ups Section */}
                                      <div className="space-y-3">
                                        <label className="block text-xs font-medium text-gray-700 mb-2">
                                          Follow-ups
                                        </label>
                                        {formData.follow_ups.map((followUp, index) => {
                                          const followUpNumber = index + 1;
                                          
                                          return (
                                          <div key={index} className="flex gap-2 items-start">
                                            <div className="flex-1">
                                              <label className="block text-xs text-gray-600 mb-1">
                                                Follow-up {followUpNumber} Date
                                              </label>
                                              <input
                                                type="date"
                                                value={followUp.date || ''}
                                                onChange={(e) => {
                                                  const updatedFollowUps = [...formData.follow_ups];
                                                  updatedFollowUps[index].date = e.target.value;
                                                  setFormData({ ...formData, follow_ups: updatedFollowUps });
                                                }}
                                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                              />
                                            </div>
                                            <div className="flex-1">
                                              <label className="block text-xs text-gray-600 mb-1">
                                                Follow-up {followUpNumber} Note
                                              </label>
                                              <input
                                                type="text"
                                                value={followUp.note}
                                                onChange={(e) => {
                                                  const updatedFollowUps = [...formData.follow_ups];
                                                  updatedFollowUps[index].note = e.target.value;
                                                  setFormData({ ...formData, follow_ups: updatedFollowUps });
                                                }}
                                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                                placeholder={`Enter note for follow-up ${followUpNumber}`}
                                              />
                                            </div>
                                            {index === formData.follow_ups.length - 1 && formData.follow_ups.length < 4 && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setFormData({
                                                    ...formData,
                                                    follow_ups: [...formData.follow_ups, { date: '', note: '' }]
                                                  });
                                                }}
                                                className="mt-6 px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300 flex items-center gap-1"
                                                title="Add Follow-up"
                                              >
                                                <Plus size={16} />
                                              </button>
                                            )}
                                          </div>
                                          );
                                        })}
                                      </div>
                                      
                                      {/* Additional Notes */}
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                          Additional Notes
                                        </label>
                                        <textarea
                                          rows={6}
                                          value={formData.notes}
                                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent resize-y"
                                          placeholder="Enter any additional notes..."
                                        />
                                      </div>
                                      <div className="flex gap-2 justify-end">
                                        {onNavigateToPreSales && editingLead && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              onNavigateToPreSales(editingLead);
                                            }}
                                            className="px-3 py-1.5 bg-orange-600 text-white rounded text-sm hover:bg-orange-700"
                                          >
                                            Go to Pre Sales
                                          </button>
                                        )}
                                        {onConvertToPotentialClient && (
                                          <button
                                            type="button"
                                            disabled={isInPotentialClients}
                                            onClick={async (e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              
                                              if (isInPotentialClients) {
                                                return;
                                              }
                                              
                                              if (window.confirm('Are you sure you want to add this company to potential clients?')) {
                                                const result = await handleConvertToPotentialClient(editingLead.id);
                                                if (result && result.success) {
                                                  setIsInPotentialClients(true);
                                                  setTimeout(async () => {
                                                    try {
                                                      const checkResult = await dashboardAPI.checkLeadInPotentialClients(editingLead.id);
                                                      if (checkResult.data && checkResult.data.id) {
                                                        // Store potential client ID if needed
                                                      }
                                                    } catch (error) {
                                                      console.error('Error re-checking potential client:', error);
                                                    }
                                                  }, 300);
                                                } else if (result && result.alreadyExists) {
                                                  setIsInPotentialClients(true);
                                                  setTimeout(async () => {
                                                    try {
                                                      const checkResult = await dashboardAPI.checkLeadInPotentialClients(editingLead.id);
                                                      if (checkResult.data && checkResult.data.id) {
                                                        // Store potential client ID if needed
                                                      }
                                                    } catch (error) {
                                                      console.error('Error re-checking potential client:', error);
                                                    }
                                                  }, 300);
                                                }
                                              }
                                            }}
                                            className={`px-3 py-1.5 rounded text-sm ${
                                              isInPotentialClients
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                                : 'bg-[#679632] text-white hover:bg-[#587f2b]'
                                            }`}
                                            style={isInPotentialClients ? { pointerEvents: 'none' } : {}}
                                          >
                                            Add to Potential Clients
                                          </button>
                                        )}
                                        <button
                                          type="submit"
                                          className="px-4 py-1.5 bg-[#679632] text-white rounded text-sm hover:bg-[#587f2b]"
                                        >
                                          Save
                                        </button>
                                      </div>
                                    </form>
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    }
                    
                    // Regular (ungrouped) lead
                    const postSalesRecord = getPostSalesForLead(lead.id);
                    const isEditing = editingLead?.id === lead.id;
                    return (
                      <React.Fragment key={lead.id}>
                        <tr 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleEdit(lead)}
                        >
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {index + 1}
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.company_name}
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="text-sm text-gray-700">
                              {(() => {
                                if (!lead.country) return <span className="text-gray-400">-</span>;
                                
                                // Normalize country name - try multiple matching strategies
                                const countryName = lead.country.trim();
                                
                                // Try exact match first
                                let countryMatch = availableCountries.find(c => 
                                  c.name === countryName
                                );
                                
                                // Try case-insensitive match
                                if (!countryMatch) {
                                  countryMatch = availableCountries.find(c => 
                                    c.name.toLowerCase() === countryName.toLowerCase()
                                  );
                                }
                                
                                // Try matching by ISO code (e.g., "US" -> "United States")
                                if (!countryMatch && countryName.length <= 3) {
                                  countryMatch = availableCountries.find(c => 
                                    c.isoCode === countryName.toUpperCase()
                                  );
                                }
                                
                                // Try partial match for common variations (e.g., "USA" -> "United States")
                                if (!countryMatch) {
                                  const normalized = countryName.toLowerCase();
                                  countryMatch = availableCountries.find(c => {
                                    const cName = c.name.toLowerCase();
                                    // Handle common variations
                                    if (normalized === 'usa' || normalized === 'u.s.a.' || normalized === 'u.s.a') {
                                      return cName === 'united states' || cName === 'united states of america';
                                    }
                                    if (normalized === 'uk' || normalized === 'u.k.' || normalized === 'u.k') {
                                      return cName === 'united kingdom' || cName.includes('united kingdom');
                                    }
                                    // Try contains match
                                    return cName.includes(normalized) || normalized.includes(cName);
                                  });
                                }
                                
                                // Return standardized name from library, or original if no match found
                                return countryMatch ? countryMatch.name : countryName;
                              })()}
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="text-sm text-gray-900 space-y-1">
                              {(() => {
                                // POC1 in sales_leads_view is bare (email/contact_no), POC2-4 use the prefixed names.
                                const preEmail = lead.email || lead.poc_email || '';
                                const prePhone = lead.contact_no || lead.poc_contact_no || '';
                                const companyGenericNumber = lead.company_generic_number || '';
                                const extraPocCount = ['poc2_name', 'poc3_name', 'poc4_name']
                                  .filter(k => lead[k] && String(lead[k]).trim()).length;
                                const psEmail = postSalesRecord?.email || '';
                                const psPhone = postSalesRecord?.contact_number || '';
                                const normalize = (v) => (v || '').trim().toLowerCase();
                                const extraEmail = psEmail && normalize(psEmail) !== normalize(preEmail) ? psEmail : '';
                                const extraPhone = psPhone && normalize(psPhone) !== normalize(prePhone) ? psPhone : '';

                                if (!lead.poc_name && !preEmail && !prePhone && !companyGenericNumber && !extraEmail && !extraPhone) {
                                  return <span className="text-gray-400 text-sm">N/A</span>;
                                }

                                return (
                                  <>
                                    {/* Pre-sales contact block — shown in blue */}
                                    {lead.poc_name && (
                                      <div className="font-medium text-blue-700">{lead.poc_name}</div>
                                    )}
                                    {preEmail && (
                                      <div className="flex items-center gap-1 text-xs">
                                        <Mail size={14} className="text-blue-500" />
                                        <span className="text-blue-600 break-all">{preEmail}</span>
                                      </div>
                                    )}
                                    {prePhone && (
                                      <div className="flex items-center gap-1 text-xs">
                                        <Phone size={14} className="text-blue-500" />
                                        <span className="text-blue-600">{prePhone}</span>
                                      </div>
                                    )}
                                    {companyGenericNumber && (
                                      <div className="flex items-center gap-1 text-xs">
                                        <Phone size={14} className="text-gray-400" />
                                        <span className="text-gray-600" title="Company generic number">{companyGenericNumber}</span>
                                      </div>
                                    )}
                                    {extraPocCount > 0 && (
                                      <div className="text-[10px] text-gray-500 italic">+{extraPocCount} more POC{extraPocCount === 1 ? '' : 's'} on file</div>
                                    )}
                                    {/* Post-sales additions — shown below in a different color */}
                                    {(extraEmail || extraPhone) && (
                                      <div className="mt-1 pt-1 border-t border-dashed border-purple-200 space-y-0.5">
                                        <div className="text-[10px] uppercase tracking-wide text-purple-500 font-semibold">Added in post-sales</div>
                                        {extraEmail && (
                                          <div className="flex items-center gap-1 text-xs">
                                            <Mail size={14} className="text-purple-500" />
                                            <span className="text-purple-700 break-all">{extraEmail}</span>
                                          </div>
                                        )}
                                        {extraPhone && (
                                          <div className="flex items-center gap-1 text-xs">
                                            <Phone size={14} className="text-purple-500" />
                                            <span className="text-purple-700">{extraPhone}</span>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {lead.company_url && (
                                <a
                                  href={normalizeUrl(lead.company_url)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Company URL"
                                >
                                  <Globe size={16} />
                                </a>
                              )}
                              {lead.linkedin_url && (
                                <a
                                  href={normalizeUrl(lead.linkedin_url)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="LinkedIn URL"
                                >
                                  <ExternalLink size={16} />
                                </a>
                              )}
                              {!lead.company_url && !lead.linkedin_url && (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="text-sm space-y-1">
                              {/* Call Status / Email Type badges */}
                              {(postSalesRecord?.call_status || postSalesRecord?.email_type) ? (
                                <>
                                  {postSalesRecord?.call_status && (
                                    <div>
                                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCallStatusBadgeColor(postSalesRecord.call_status)}`}>
                                        {postSalesRecord.call_status}
                                      </span>
                                    </div>
                                  )}
                                  {postSalesRecord?.email_type && (
                                    <div>
                                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEmailTypeBadgeColor(postSalesRecord.email_type)}`}>
                                        {postSalesRecord.email_type}
                                      </span>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                              {/* Timezone with location pin */}
                              {postSalesRecord?.time_zone && (
                                <div className="flex items-center gap-1 text-xs text-gray-600">
                                  <MapPin size={12} className="text-gray-400" />
                                  <span>{postSalesRecord.time_zone}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {postSalesRecord ? (
                              <div className="relative">
                                <select
                                  value={postSalesRecord.stage}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleStageUpdate(postSalesRecord.id, e.target.value);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`text-xs font-semibold rounded-full px-3 py-1.5 border-0 appearance-none pr-8 cursor-pointer ${getStageBadgeColor(postSalesRecord.stage)}`}
                                >
                                  <option value="followup">Follow-up</option>
                                  <option value="done">Done</option>
                                  <option value="in_progress">In Progress</option>
                                  <option value="pending">Pending</option>
                                </select>
                                <ChevronDown size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-600" />
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {(() => {
                              const bdeName = postSalesRecord?.post_sales_bde_name;
                              return bdeName
                                ? <span className="text-sm text-gray-900">{bdeName}</span>
                                : <span className="text-gray-400 text-xs italic">Not assigned</span>;
                            })()}
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(lead);
                              }}
                              className={`flex items-center gap-1 text-sm font-medium ${
                                postSalesRecord 
                                  ? 'text-red-600 hover:text-red-700' 
                                  : 'text-green-600 hover:text-green-700'
                              }`}
                            >
                              {postSalesRecord ? (
                                <>
                                  <Edit size={14} />
                                  <span>Edit</span>
                                </>
                              ) : (
                                <>
                                  <CheckCircle size={14} />
                                  <span>Log</span>
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                        {isEditing && (
                          <tr className="bg-green-50">
                            <td colSpan="9" className="px-4 py-4">
                              <form onSubmit={handleSave} className="space-y-3">
                                <div className="flex justify-between items-center mb-3">
                                  <h4 className="text-sm font-semibold text-gray-900">
                                    Edit Post Sales - {editingLead.company_name}
                                  </h4>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingLead(null);
                                      setFormData({
                                        lead_id: '',
                                        contact_number: '',
                                        time_zone: '',
                                        call_status: '',
                                        email_type: '',
                                        email: '',
                                        stage: 'followup',
                                        notes: ''
                                      });
                                    }}
                                    className="text-gray-400 hover:text-gray-600 text-sm"
                                  >
                                    ✕ Cancel
                                  </button>
                                </div>
                                
                                {/* Pre-Sales Contact Reference (POCs prioritized over company generic number) */}
                                <PreSalesContactPanel lead={editingLead} />

                                {/* Post Sales BDE Display */}
                                {(() => {
                                  const currentRecord = getPostSalesForLead(editingLead?.id);
                                  const bdeName = currentRecord?.post_sales_bde_name || postSalesBDE?.name || 'Surabhi Anand';
                                  return (
                                    <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-gray-700">Post Sales Handler:</span>
                                        <span className="text-sm font-semibold text-blue-700">{bdeName}</span>
                                        <span className="text-xs text-gray-500">(BDE ID: {currentRecord?.post_sales_bde_id || postSalesBDE?.id || 12})</span>
                                      </div>
                                    </div>
                                  );
                                })()}
                                
                                <div className="grid grid-cols-4 gap-4">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Contact Number
                                    </label>
                                    <input
                                      type="text"
                                      value={formData.contact_number}
                                      onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                      placeholder="Enter contact number"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Time Zone
                                    </label>
                                    <select
                                      value={formData.time_zone}
                                      onChange={(e) => setFormData({ ...formData, time_zone: e.target.value })}
                                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                    >
                                      <option value="">Select time zone</option>
                                      {TIME_ZONES.map(tz => (
                                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Call Status
                                    </label>
                                    <select
                                      value={formData.call_status}
                                      onChange={(e) => setFormData({ ...formData, call_status: e.target.value })}
                                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                    >
                                      <option value="">Select call status</option>
                                      <option value="answered">Answered</option>
                                      <option value="not answered">Not Answered</option>
                                      <option value="voicemail">Voicemail</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Email Type
                                    </label>
                                    <select
                                      value={formData.email_type}
                                      onChange={(e) => setFormData({ ...formData, email_type: e.target.value })}
                                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                    >
                                      <option value="">Select email type</option>
                                      <option value="generic">Generic</option>
                                      <option value="personal">Personal</option>
                                      <option value="provided">Provided</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Email
                                    </label>
                                    <input
                                      type="email"
                                      value={formData.email}
                                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                      placeholder="Enter email address"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Stage
                                    </label>
                                    <select
                                      value={formData.stage}
                                      onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                    >
                                      <option value="followup">Follow-up</option>
                                      <option value="done">Done</option>
                                      <option value="in_progress">In Progress</option>
                                      <option value="pending">Pending</option>
                                    </select>
                                  </div>
                                </div>
                                
                                {/* Follow-ups Section */}
                                <div className="space-y-3">
                                  <label className="block text-xs font-medium text-gray-700 mb-2">
                                    Follow-ups
                                  </label>
                                  {formData.follow_ups.map((followUp, index) => {
                                    const followUpNumber = index + 1;
                                    
                                    return (
                                    <div key={index} className="flex gap-2 items-start">
                                      <div className="flex-1">
                                        <label className="block text-xs text-gray-600 mb-1">
                                          Follow-up {followUpNumber} Date
                                        </label>
                                        <input
                                          type="date"
                                          value={followUp.date || ''}
                                          onChange={(e) => {
                                            const updatedFollowUps = [...formData.follow_ups];
                                            updatedFollowUps[index].date = e.target.value;
                                            setFormData({ ...formData, follow_ups: updatedFollowUps });
                                          }}
                                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                        />
                                      </div>
                                      <div className="flex-1">
                                        <label className="block text-xs text-gray-600 mb-1">
                                          Follow-up {index + 1} Note
                                        </label>
                                        <input
                                          type="text"
                                          value={followUp.note}
                                          onChange={(e) => {
                                            const updatedFollowUps = [...formData.follow_ups];
                                            updatedFollowUps[index].note = e.target.value;
                                            setFormData({ ...formData, follow_ups: updatedFollowUps });
                                          }}
                                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                                          placeholder={`Enter note for follow-up ${index + 1}`}
                                        />
                                      </div>
                                      {index === formData.follow_ups.length - 1 && formData.follow_ups.length < 4 && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setFormData({
                                              ...formData,
                                              follow_ups: [...formData.follow_ups, { date: '', note: '' }]
                                            });
                                          }}
                                          className="mt-6 px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300 flex items-center gap-1"
                                          title="Add Follow-up"
                                        >
                                          <Plus size={16} />
                                        </button>
                                      )}
                                    </div>
                                    );
                                  })}
                                </div>
                                
                                {/* Additional Notes */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Additional Notes
                                  </label>
                                  <textarea
                                    rows={6}
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent resize-y"
                                    placeholder="Enter any additional notes..."
                                  />
                                </div>
                                <div className="flex gap-2 justify-end">
                                  {onNavigateToPreSales && editingLead && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onNavigateToPreSales(editingLead);
                                      }}
                                      className="px-3 py-1.5 bg-orange-600 text-white rounded text-sm hover:bg-orange-700"
                                    >
                                      Go to Pre Sales
                                    </button>
                                  )}
                                  {onConvertToPotentialClient && (
                                    <button
                                      type="button"
                                      disabled={isInPotentialClients}
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        
                                        // Double-check state before proceeding
                                        if (isInPotentialClients) {
                                          return;
                                        }
                                        
                                        if (window.confirm('Are you sure you want to add this company to potential clients?')) {
                                          const result = await handleConvertToPotentialClient(editingLead.id);
                                          if (result && result.success) {
                                            // Immediately set state to disable button
                                            setIsInPotentialClients(true);
                                            // Re-check from database
                                            setTimeout(async () => {
                                              try {
                                                const checkResult = await dashboardAPI.checkLeadInPotentialClients(editingLead.id);
                                                if (checkResult.data && checkResult.data.id) {
                                                  // Store potential client ID if needed
                                                }
                                              } catch (error) {
                                                console.error('Error re-checking potential client:', error);
                                              }
                                            }, 300);
                                          } else if (result && result.alreadyExists) {
                                            // Backend detected duplicate
                                            setIsInPotentialClients(true);
                                            setTimeout(async () => {
                                              try {
                                                const checkResult = await dashboardAPI.checkLeadInPotentialClients(editingLead.id);
                                                if (checkResult.data && checkResult.data.id) {
                                                  // Store potential client ID if needed
                                                }
                                              } catch (error) {
                                                console.error('Error re-checking potential client:', error);
                                              }
                                            }, 300);
                                          }
                                        }
                                      }}
                                      className={`px-3 py-1.5 rounded text-sm ${
                                        isInPotentialClients 
                                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                                          : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                                      }`}
                                      title={isInPotentialClients ? 'Already in Potential Clients' : 'Add to Potential Clients'}
                                      aria-disabled={isInPotentialClients}
                                    >
                                      Add to Potential Clients
                                    </button>
                                  )}
                                  <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-3 py-1.5 bg-[#679632] text-white rounded text-sm hover:bg-[#587f2b] disabled:opacity-50"
                                  >
                                    {loading ? 'Saving...' : 'Save'}
                                  </button>
                                </div>
                              </form>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostSales;
